import React, { useEffect, useState, useCallback } from "react";
import {
  MotifErrorMessage,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import Button from "@/components/Form/Button/Button";
import { AddDebtorFormType } from "./type";
import "./FormModal.scss";
import {
  compareNumeric,
  isNumeric,
  isNumericOrNull,
} from "@/utility/validation";
import ControlInputBoxNumber from "@/components/Form/InputBox/ControlInputBoxNumber";
import {
  useGetRateTierListByCaseQuery,
  useUpdateRateTierMutation,
} from "../calculationStepReducer";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import {
  CANCEL_BUTTON_VARIANT,
  SAVE_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import {
  capitalizeAllWordsFirstChar,
  capitalizeWordsInSentence,
} from "@/utility/common";
import { useParams } from "react-router-dom";

type Props = {
  isEditVisible: boolean;
  setEditVisible: React.Dispatch<React.SetStateAction<boolean>>;
  formData?: AddDebtorFormType | null;
  caseId: string;
  stepId: string;
  refetch: any;
  editData: any;
  setSuccessMsg: any;
  resolvedStatus: boolean;
};

export default function RateTierEditForm({
  isEditVisible,
  setEditVisible,
  stepId,
  editData,
  setSuccessMsg,
  refetch,
  resolvedStatus,
}: Props) {
  const { t } = useTranslation();
  const [isVisibleCancel, setIsVisibleCancel] = useState(false);
  const [showMessage, setShowMessage] = useState({
    show: false,
    type: "error",
    message: "",
  });
  let { caseId } = useParams();
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<any>({
    defaultValues: {
      maxAmount:
        editData?.maxAmount === 0 ? "0" : editData?.maxAmount?.toString(),
      minAmount:
        editData?.minAmount === 0 ? "0" : editData?.minAmount?.toString(),
      rate: editData?.rate?.toString(),
      adjustment: editData?.adjustment?.toString(),
      rateTierGroupBy: editData?.rateTierGroupBy?.toString(),
    },
  });
  const errorList = Object.keys(errors);
  const errorsShow = capitalizeAllWordsFirstChar(
    capitalizeWordsInSentence(errorList.join(", "))
  );
  const [errorMessage, setErrorMessage] = useState("");
  const { data: rateTireData } = useGetRateTierListByCaseQuery(
    {
      CalculationStepId: stepId,
      CaseId: caseId || "",
    },
    { skip: !caseId }
  );
  const resetData = useCallback(() => {
    reset({
      maxAmount:
        editData?.maxAmount === 0 ? "0" : editData?.maxAmount?.toString(),
      minAmount:
        editData?.minAmount === 0 ? "0" : editData?.minAmount?.toString(),
      rate: editData?.rate?.toString(),
      adjustment: editData?.adjustment?.toString(),
      rateTierGroupBy: editData?.rateTierGroupBy?.toString(),
    });
  }, [editData, reset]);

  useEffect(() => {
    resetData();
  }, [editData, resetData]);

  const [UpdateRateTier, { isLoading }] = useUpdateRateTierMutation();
  const setFormat = (value: any) => {
    return value === "" || value == null || value === 0 ? null : Number(value);
  };
  function validateMinMaxTire(formData: any) {
    let trig = true;
    if (formData?.maxAmount === "" && formData?.minAmount === "") {
      setErrorMessage(
        t(
          "pages.distributions.distributionStepsTab.rateTierTab.validationMessages.sameMaxMinValueMessage"
        )
      );
      trig = false;
    } else if (
      (formData?.minAmount === "" && Number(formData?.maxAmount) > 0) ||
      (formData?.maxAmount === "" && Number(formData?.minAmount) < 0) ||
      (Math.sign(Number(formData?.maxAmount)) !==
        Math.sign(Number(formData?.minAmount)) &&
        Math.sign(Number(formData?.maxAmount)) !== 0 &&
        Math.sign(Number(formData?.minAmount)) !== 0)
    ) {
      setErrorMessage(
        t(
          "pages.distributions.distributionStepsTab.rateTierTab.validationMessages.minMaxValueFormateMeessage"
        )
      );
      trig = false;
    } else if (compareNumeric(formData?.maxAmount, formData?.minAmount) < 0) {
      setErrorMessage(
        t(
          "pages.distributions.distributionStepsTab.rateTierTab.validationMessages.minvalueRangeMesaage"
        )
      );
      trig = false;
    } else {
      let maxAmount =
        formData?.maxAmount !== "" &&
        formData?.maxAmount !== undefined &&
        formData?.maxAmount !== null
          ? Number(formData?.maxAmount)
          : null;
      let minAmount =
        formData?.minAmount !== "" &&
        formData?.minAmount !== undefined &&
        formData?.minAmount !== null
          ? Number(formData?.minAmount)
          : null;
      trig = rateTireData?.find((item: any) => {
        if (
          item.maxAmount === maxAmount &&
          item.minAmount === minAmount &&
          item.id !== editData?.id
        ) {
          return true;
        }
        return false;
      });
      if (trig) {
        setErrorMessage(
          t(
            "pages.distributions.distributionStepsTab.rateTierTab.validationMessages.minMaxAlreadyExistsMessage"
          )
        );
      }
      trig = !trig;
    }
    return trig;
  }

  const onSubmit = async (data: any) => {
    let formData = { ...data };
    formData.maxAmount =
      formData?.maxAmount === undefined ? null : formData?.maxAmount;
    formData.minAmount =
      formData?.minAmount === undefined ? null : formData?.minAmount;
    if (validateMinMaxTire(formData)) {
      setErrorMessage("");
      let formData = {
        id: editData?.id,
        calculationStepId: stepId,
        maxAmount: setFormat(data?.maxAmount),
        minAmount: setFormat(data?.minAmount),
        rate: data?.rate === "" ? 1 : Number(data?.rate),
        adjustment: data?.adjustment === "" ? null : Number(data?.adjustment),
        // rateTierGroupBy: Number(data?.rateTierGroupBy),
      };

      const response = await UpdateRateTier(formData).unwrap();
      if (response) {
        setEditVisible(false);
        setSuccessMsg(true);
        refetch();
        //reset()
      } else {
        setEditVisible(false);
      }
    }
  };
  const cancelBtnApproveHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setEditVisible(false);
    setIsVisibleCancel(false);
    resetData();
    setShowMessage({ ...showMessage, show: false, type: "error" });
  };

  const cancelBtnRejectHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setIsVisibleCancel(false);
    setShowMessage({ ...showMessage, show: false, type: "error" });
  };
  return (
    <>
      <MotifModal
        onClose={() => {
          if (isDirty) {
            setIsVisibleCancel(true);
          } else {
            setIsVisibleCancel(false);
            setEditVisible(false);
          }
        }}
        show={isEditVisible}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        {/*@ts-ignore*/}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="formModalWrapper">
            <MotifModalHeader>
              <span className="discard_heading">
                {t(
                  "pages.distributions.distributionStepsTab.rateTierTab.formLabels.editTitle"
                )}
              </span>
            </MotifModalHeader>
            <MotifModalBody>
              <div className="formModal">
                {/* <div className="motif-row mar-t-20">
                  <div className="motif-col-md-5">
                    <ControlSelect
                      name={"rateTierGroupBy"}
                      //@ts-ignore
                      control={control}
                      required={true}
                      showLabel={true}
                      readonly={true}
                      label={t("Type")}
                      //@ts-ignore
                      placeholder={"placeholder"}
                      options={[
                        { label: "Select Type", value: "" },
                        { label: "Creditor", value: "0" },
                        { label: "Claim", value: "1" },
                        { label: "Creditor/Assignee", value: "2" },
                      ]}
                    />
                  </div>
                </div> */}
                <div className="motif-row mar-t-20">
                  <div className="motif-col-md-5">
                    <ControlInputBoxNumber
                      name={"maxAmount"}
                      // @ts-ignore
                      control={control}
                      // @ts-ignore
                      setValue={setValue}
                      required={false}
                      isTextArea={false}
                      label={
                        "pages.distributions.distributionStepsTab.rateTierTab.formLabels.tierMaxAmount"
                      }
                      showLabel={true}
                      isNumber={true}
                      digitAfterDecimal={8}
                      isAmount={true}
                      onChangeAmount={() => {
                        setErrorMessage("");
                      }}
                      validate={isNumericOrNull}
                      readonly={resolvedStatus}
                    />
                  </div>
                </div>
                <div className="motif-row mar-t-20">
                  <div className="motif-col-md-5">
                    <ControlInputBoxNumber
                      name={"minAmount"}
                      // @ts-ignore
                      control={control}
                      required={false}
                      isTextArea={false}
                      label={
                        "pages.distributions.distributionStepsTab.rateTierTab.formLabels.tierFloorAmount"
                      }
                      showLabel={true}
                      isNumber={true}
                      digitAfterDecimal={8}
                      isAmount={true}
                      onChangeAmount={() => {
                        setErrorMessage("");
                      }}
                      validate={isNumericOrNull}
                      readonly={resolvedStatus}
                    />
                  </div>
                </div>
                <div className="motif-row mar-t-20">
                  <div className="motif-col-md-5">
                    <ControlInputBoxNumber
                      name={"rate"}
                      // @ts-ignore
                      control={control}
                      required={true}
                      isTextArea={false}
                      label={
                        "pages.distributions.distributionStepsTab.rateTierTab.formLabels.rate"
                      }
                      showLabel={true}
                      isNumber={true}
                      digitAfterDecimal={8}
                      isAmount={true}
                      onChangeAmount={() => {}}
                      validate={isNumeric}
                      readonly={resolvedStatus}
                    />
                    {errors["rate"] && errors["rate"].type === "required" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.distributions.distributionStepsTab.rateTierTab.validationMessages.rateRequired"
                        )}
                      </MotifErrorMessage>
                    )}
                  </div>
                </div>
                <div className="motif-row mar-t-20">
                  <div className="motif-col-md-5">
                    <ControlInputBoxNumber
                      name={"adjustment"}
                      // @ts-ignore
                      control={control}
                      required={false}
                      isTextArea={false}
                      label={
                        "pages.distributions.distributionStepsTab.rateTierTab.formLabels.adjustment"
                      }
                      showLabel={true}
                      isNumber={true}
                      digitAfterDecimal={8}
                      isAmount={true}
                      onChangeAmount={() => {}}
                      validate={isNumericOrNull}
                      readonly={resolvedStatus}
                    />
                    {errors["adjustment"] &&
                      errors["adjustment"].type === "required" && (
                        <MotifErrorMessage>
                          {t(
                            "pages.distributions.distributionStepsTab.rateTierTab.validationMessages.adjustmentRequired"
                          )}
                        </MotifErrorMessage>
                      )}
                  </div>
                </div>
                <div className="motif-row mar-l-5 mar-t-10">
                  <MotifErrorMessage>
                    {errorMessage && (
                      <MotifErrorMessage>{errorMessage}</MotifErrorMessage>
                    )}

                    {showMessage.show && showMessage.message && isDirty}
                    {errorList.length > 0 &&
                      t(
                        "pages.distributions.distributionStepsTab.classFilterTab.validationMessageWithEmpty",
                        { fieldNames: errorsShow }
                      )}
                  </MotifErrorMessage>
                </div>
              </div>
            </MotifModalBody>
            <MotifModalFooter>
              <div className="button-container-add">
                <Button
                  variant={SAVE_BUTTON_VARIANT}
                  label={t("global.globalActions.save")}
                  loading={isLoading}
                  disabled={isLoading || resolvedStatus}
                />
                <Button
                  label={t("global.globalActions.cancel")}
                  type="button"
                  variant={CANCEL_BUTTON_VARIANT}
                  className="cancelBtn"
                  onClickHandler={() => {
                    if (isDirty) {
                      setIsVisibleCancel(true);
                    } else {
                      setIsVisibleCancel(false);
                      setEditVisible(false);
                    }
                  }}
                />
              </div>
            </MotifModalFooter>
          </div>
        </form>
      </MotifModal>
      <ConfirmationModal
        isVisible={isVisibleCancel}
        setIsVisible={setIsVisibleCancel}
        cancelBtnApproveHandler={cancelBtnApproveHandler}
        cancelBtnRejectHandler={cancelBtnRejectHandler}
      />
    </>
  );
}
